import { GraphQLFormattedError, print } from 'graphql';
import {
  EdmDocumentationVersionViewCreateDocument,
  EdmDocumentationVersionViewIncrementDocument,
} from '@lib/graphql-common';
import { hasAuthenticationError } from '@lib/utils/authErrorHandler';
import { emitSessionExpired } from '@lib/utils/sessionExpiredEvent';
import { captureException } from '@lib/utils/captureException';

const { VITE_API_URL, VITE_AUTH_SCOPE } = import.meta.env;

/**
 * Fire-and-forget transport for document-view mutations that have to survive
 * the page going away.
 *
 * Apollo cannot be used for this. Every operation except a small allowlist goes
 * through `BatchHttpLink` with a 20ms batch interval, and that timer does not
 * get to run while the browser is tearing the page down — the mutation would be
 * dropped precisely in the case we are trying to cover.
 *
 * `keepalive` tells the browser to complete the request even after the document
 * is gone. `sendBeacon` would be the other candidate but cannot set headers, and
 * the API needs `X-Auth-Scope`. Auth itself rides on cookies (`credentials:
 * 'include'`), so there is no token to plumb through here.
 *
 * Requests are sent unbatched. An HTTP error status or a GraphQL `errors`
 * payload rejects the returned promise — the caller needs to tell a landed
 * flush from a lost one to roll back optimistic credit and retry — but nothing
 * here ever surfaces to the user: this is telemetry, not a user-visible write.
 */
async function post<T>(
  query: string,
  variables: Record<string, unknown>,
): Promise<T | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (VITE_AUTH_SCOPE) headers['X-Auth-Scope'] = VITE_AUTH_SCOPE;

  const response = await fetch(`${VITE_API_URL}/graphql`, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    mode: 'cors',
    headers,
    body: JSON.stringify({ query, variables }),
  });
  if (response.status === 401) {
    // Mirrors ApolloClient's errorLink: a 401 here means the session has
    // expired same as it would for any other mutation, and nothing else on
    // this path would otherwise trigger the sign-out redirect.
    emitSessionExpired();
    throw new Error('Documentation view beacon failed: HTTP 401');
  }
  if (!response.ok) {
    const error = new Error(
      `Documentation view beacon failed: HTTP ${response.status}`,
    );
    captureException(error);
    throw error;
  }
  const body = await response.json();
  // GraphQL transports errors in a 200 response; treating it as success would
  // mark the flushed seconds as credited and silently lose them.
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    const graphQLErrors = body.errors as GraphQLFormattedError[];
    const error = new Error(
      graphQLErrors[0]?.message || 'Documentation view beacon rejected',
    );
    if (hasAuthenticationError(graphQLErrors)) {
      emitSessionExpired();
    } else {
      captureException(error);
    }
    throw error;
  }
  return (body?.data as T) ?? null;
}

/** Creates the view record. Resolves with its id; rejects if it did not land. */
export async function sendViewCreateBeacon(
  documentationVersionId: string,
  viewedAt: string,
  readingDurationSeconds: number,
): Promise<string | null> {
  const data = await post<{
    documentationVersionViewCreate?: { id?: string } | null;
  }>(print(EdmDocumentationVersionViewCreateDocument), {
    documentationVersionId,
    viewedAt,
    readingDurationSeconds,
  });
  return data?.documentationVersionViewCreate?.id ?? null;
}

/** Adds engagement time to an existing view record. */
export async function sendViewIncrementBeacon(
  id: string,
  readingDurationSeconds: number,
): Promise<void> {
  await post(print(EdmDocumentationVersionViewIncrementDocument), {
    id,
    readingDurationSeconds,
  });
}
